import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { Project } from "../../../interfaces/Project";
import { useLoading } from "../../context/LoadingContext";
import { useProject } from "../../context/ProjectContext";
import { ediphiPalette } from "../../styles/colors";
import { useProjectActions } from "../../hooks/useProjectActions";
import { useProjectSelection } from "../../hooks/useProjectSelection";
import CardSkeleton from "../../shared/loaders/CardSkeleton";
import { formatLocation } from "./formatLocation";
import SearchField from "../../shared/inputs/SearchField";
import DividerAccordionLayout from "../../shared/accordions/DividerAccordionLayout";
import { DetailCard } from "../../shared/cards/DetailCard";
import { ProjectDetailRow } from "./ProjectDetailRow";
import EmptyStateCard from "../../shared/cards/EmptyStateCard";
import { UiStateCacheKey } from "../../../excel/workbook/cacheKeys";

const MAX_PROJECTS = 40;
const SKELETON_COUNT = 4;

const groupCacheKey = (title: string): UiStateCacheKey => `projectGroupExpanded_${title}`;

const ProjectSelector: React.FC = () => {
  const { selectedProject } = useProject();
  const { withLoading } = useLoading();
  const { getAllProjectData } = useProjectActions();
  const { selectProject } = useProjectSelection();

  const [projects, setProjects] = useState<Project[]>([]);
  const [recentlyViewedProjectIds, setRecentlyViewedProjectIds] = useState<string[]>([]);
  const [pinnedProjectIds, setPinnedProjectIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const loadProjectsAndSettings = async () => {
      try {
        await withLoading(async () => {
          const { projects, recentlyViewedProjectIds, pinnedProjectIds } = await getAllProjectData();
          setProjects(projects);
          setRecentlyViewedProjectIds(recentlyViewedProjectIds);
          setPinnedProjectIds(pinnedProjectIds);
        });
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectsAndSettings();
  }, []);

  const matchesSearch = (project: Project, query: string): boolean => {
    if (!query.trim()) return true;
    const lowerQuery = query.toLowerCase();
    const searchableFields = [project.name, project.client_name, project.address].filter(Boolean);
    return searchableFields.some((field) => field?.toLowerCase().includes(lowerQuery));
  };

  const filteredProjects = useMemo(
    () => projects.filter((project) => matchesSearch(project, searchQuery)),
    [projects, searchQuery]
  );

  const recentlyViewedProjects = useMemo(
    () => filteredProjects.filter((project) => recentlyViewedProjectIds.includes(project.id)),
    [filteredProjects, recentlyViewedProjectIds]
  );

  // Excludes anything already in Recently Viewed, so a pinned project it also matches appears once.
  const pinnedProjects = useMemo(
    () =>
      filteredProjects.filter(
        (project) => pinnedProjectIds.includes(project.id) && !recentlyViewedProjectIds.includes(project.id)
      ),
    [filteredProjects, pinnedProjectIds, recentlyViewedProjectIds]
  );

  const otherProjects = useMemo(
    () =>
      filteredProjects
        .filter((project) => !recentlyViewedProjectIds.includes(project.id) && !pinnedProjectIds.includes(project.id))
        .slice(0, MAX_PROJECTS),
    [filteredProjects, recentlyViewedProjectIds, pinnedProjectIds]
  );

  // Selecting a project clears the workbook and re-tabulates .metadata, so the list has to stop
  // taking clicks — a second one would start a concurrent switch of the same invalidation boundary.
  const handleProjectClick = async (project: Project) => {
    setSwitching(true);
    try {
      await selectProject(project);
    } finally {
      // selectProject swallows its own failure and leaves no project selected, so this component
      // survives that path and has to release the lock itself.
      setSwitching(false);
    }
  };

  if (selectedProject) {
    return null;
  }

  const renderProjectCard = (project: Project) => (
    <DetailCard
      key={project.id}
      title={project.name}
      accentColor={project.color}
      onClick={() => void handleProjectClick(project)}
    >
      {formatLocation(project)}
      {project.region && typeof project.region !== "string" && (
        <ProjectDetailRow label="Region" value={project.region.name} />
      )}
      {project.client_name && <ProjectDetailRow label="Client" value={project.client_name} />}
    </DetailCard>
  );

  const renderProjectSection = (title: string, projectList: Project[]) => {
    if (projectList.length === 0) return null;

    return (
      <DividerAccordionLayout key={title} title={title} cacheKey={groupCacheKey(title)}>
        {projectList.map(renderProjectCard)}
      </DividerAccordionLayout>
    );
  };

  const renderBody = () => {
    if (loading || switching) {
      return Array.from({ length: SKELETON_COUNT }, (_, index) => <CardSkeleton key={index} height={104} />);
    }

    if (projects.length === 0) return <EmptyStateCard text="No projects available" />;
    if (filteredProjects.length === 0) return <EmptyStateCard text="No results" />;

    return (
      <>
        {renderProjectSection("Recently Viewed", recentlyViewedProjects)}
        {renderProjectSection("Favorites", pinnedProjects)}
        {renderProjectSection("All Projects", otherProjects)}
      </>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Sticky so the search stays reachable while scrolling a long project list. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          pt: "20px",
          pb: "6px",
          backgroundColor: ediphiPalette.mainBackground,
        }}
      >
        <SearchField value={searchQuery} onChange={setSearchQuery} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: "20px" }}>{renderBody()}</Box>
    </Box>
  );
};

export default ProjectSelector;
